import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DesignCard from "@/components/DesignCard";
import { ArrowRight, Sparkles, ShoppingBag, Zap } from "lucide-react";

export default function Home() {
  const [featuredDesigns, setFeaturedDesigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        const response = await axios.get(`${API}/designs/featured`);
        setFeaturedDesigns(response.data);
      } catch (e) {
        console.error("Failed to fetch featured designs");
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="home-page">
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section relative container-padding pt-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="relative z-10">
              <p className="overline mb-4 animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
                PRINT-ON-DEMAND DESIGNS
              </p>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight animate-fade-in-up opacity-0"
                style={{ fontFamily: 'Outfit, sans-serif', animationDelay: '0.2s', animationFillMode: 'forwards' }}
              >
                Designs that
                <span className="block font-semibold">make statements.</span>
              </h1>
              <p 
                className="mt-6 text-lg text-zinc-600 max-w-lg animate-fade-in-up opacity-0"
                style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
              >
                Discover premium t-shirt designs or create your own with AI. 
                From minimal to bold, find the perfect design for your brand.
              </p>

              <div 
                className="mt-8 flex flex-wrap gap-4 animate-fade-in-up opacity-0"
                style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
              >
                <Link to="/marketplace" className="btn-primary flex items-center gap-2" data-testid="hero-browse-btn">
                  Browse Designs
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/generator" className="btn-accent flex items-center gap-2" data-testid="hero-generate-btn">
                  <Sparkles className="w-4 h-4" />
                  Generate with AI
                </Link>
              </div>

              {/* Stats */}
              <div 
                className="mt-12 flex gap-12 animate-fade-in-up opacity-0"
                style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}
              >
                <div>
                  <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>500+</p>
                  <p className="text-sm text-zinc-500">Unique Designs</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>AI</p>
                  <p className="text-sm text-zinc-500">Powered Generation</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Instant</p>
                  <p className="text-sm text-zinc-500">Downloads</p>
                </div>
              </div>
            </div>

            {/* Right Image Grid */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div 
                    className="aspect-square rounded-2xl overflow-hidden animate-fade-in-up opacity-0 floating"
                    style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1621059591080-d5db06386b53?w=400"
                      alt="T-shirt design"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div 
                    className="aspect-[4/3] rounded-2xl overflow-hidden animate-fade-in-up opacity-0"
                    style={{ animationDelay: '0.5s', animationFillMode: 'forwards', animationDuration: '6s' }}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1557683316-973673baf926?w=400"
                      alt="Abstract design"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div 
                    className="aspect-[4/3] rounded-2xl overflow-hidden animate-fade-in-up opacity-0"
                    style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400"
                      alt="Geometric design"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div 
                    className="aspect-square rounded-2xl overflow-hidden animate-fade-in-up opacity-0 floating"
                    style={{ animationDelay: '0.6s', animationFillMode: 'forwards', animationDuration: '5s' }}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?w=400"
                      alt="Tokyo neon"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing container-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="overline mb-4">WHY VANTAGE</p>
          <h2 
            className="text-3xl sm:text-4xl font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Everything you need for POD success.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="p-8 border border-zinc-200 rounded-2xl hover:border-zinc-900 transition-colors">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                AI Design Generator
              </h3>
              <p className="text-zinc-600">
                Create unique designs in seconds with our AI-powered generator. 
                Just describe your vision and watch it come to life.
              </p>
            </div>

            <div className="p-8 border border-zinc-200 rounded-2xl hover:border-zinc-900 transition-colors">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-6">
                <ShoppingBag className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Curated Marketplace
              </h3>
              <p className="text-zinc-600">
                Browse hundreds of premium designs across multiple categories. 
                Each design is ready for print-on-demand production.
              </p>
            </div>

            <div className="p-8 border border-zinc-200 rounded-2xl hover:border-zinc-900 transition-colors">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Instant Downloads
              </h3>
              <p className="text-zinc-600">
                Purchase and download high-resolution files instantly. 
                Ready to upload to your favorite POD platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Designs */}
      <section className="section-spacing container-padding" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="overline mb-4">TRENDING NOW</p>
              <h2 
                className="text-3xl sm:text-4xl font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Featured Designs
              </h2>
            </div>
            <Link 
              to="/marketplace" 
              className="hidden md:flex items-center gap-2 text-sm font-medium hover:text-indigo-600 transition-colors"
              data-testid="view-all-link"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton-pulse rounded-lg">
                  <div className="aspect-square"></div>
                  <div className="p-4 space-y-2">
                    <div className="h-4 rounded"></div>
                    <div className="h-3 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredDesigns.slice(0, 8).map((design, idx) => (
                <div
                  key={design.id}
                  className="animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${0.1 * idx}s`, animationFillMode: 'forwards' }}
                >
                  <DesignCard design={design} />
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Link to="/marketplace" className="btn-secondary inline-flex items-center gap-2">
              View All Designs
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing container-padding bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to create something unique?
          </h2>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl mx-auto">
            Use our AI generator to create custom designs or explore our marketplace 
            for ready-to-use premium designs.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/generator" className="btn-accent flex items-center gap-2" data-testid="cta-generate-btn">
              <Sparkles className="w-4 h-4" />
              Start Generating
            </Link>
            <Link to="/marketplace" className="bg-white text-zinc-900 rounded-full px-8 py-3 font-medium hover:bg-zinc-100 transition-colors">
              Browse Marketplace
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
